import React, { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useDataProvider } from 'react-admin';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import FormLabel from '@material-ui/core/FormLabel';

import { ToLocation } from 'icons';
import { get } from 'lodash';

import { Group, GroupContent, GroupItem, GroupTitle } from 'components/common/Group';

import { Checkbox, CountriesSelect, FormErrorLabel, Input, StateSelect } from 'components/form';
import { required } from 'components/form/validationRules';

import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import useProfile from 'hooks/useProfile';
import SubmitButton from 'components/buttons/SubmitButton';
import AddressDistributor from 'components/form/AddressDistributor';
import { getDomesticCountryCode } from 'services/helpers/countryCode';
import getAll from 'services/helpers/getAll';

import { ReactComponent as BankIcon } from 'assets/general/icons/Bank.svg';
import useCustomNotify from 'hooks/useCustomNotify';

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: '32px 24px',

    '& .MuiButton-containedSecondary': {
      backgroundColor: theme.palette.secondary.light,

      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },
  dialogButton: {
    textTransform: 'none',
  },
  button: {
    color: theme.palette.secondary.contrastText,
    border: `1px solid #E0E0E0`,
  },
  addressGroupTitle: {
    margin: 0,
    padding: theme.spacing(1.5, 2),
  },
  groupContent: {
    padding: theme.spacing(0, 2),
  },
  bankListContainer: {
    padding: theme.spacing(0, 2),
  },
  group: {
    paddingBottom: theme.spacing(1),
  },
  saveCard: {
    paddingLeft: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  checkboxRoot: {
    margin: 0,
  },
  secondaryText: {
    color: '#3C3C43',
    opacity: 0.6,
    padding: theme.spacing(0, 2),
    fontSize: '14px',
  },
  error: {
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}));

const ADD_BALANCE = 'Add balance';
const ADD_ACCOUNT = 'Add new account';

let timeoutId;
const TIMEOUT = 10000;

interface Props {
  onCancel: () => void;
  amount?: number;
  paymentLoader: React.ReactElement;
  onClose: () => void;
  pageTitle: string;
  stripe: any;
  onAccountConnect?: () => void;
}

const PaymentSection = ({
  onCancel,
  onClose,
  clientSecret,
  paymentLoader,
  pageTitle,
  values,
  onAccountConnect,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dataProvider = useDataProvider();
  const classes = useStyles();
  const notify = useCustomNotify();

  const [buttonEnabled, setButtonEnabled] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const resetTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  const setPaymentTimeout = () =>
    setTimeout(() => {
      resetTimeout();
      setProcessing(false);
      onClose();
    }, TIMEOUT);

  const getPaymentReview = async (id: string) => {
    try {
      const {
        data: { achUserMessage, status },
      } = await dataProvider.getOne('paymentReview', { id });

      if (status === 'processing' && achUserMessage) {
        notify(achUserMessage);
        setProcessing(false);
        resetTimeout();
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));

      return timeoutId ? await getPaymentReview(id) : null;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while getting payment review';
      notify(errorMessage, 'error');
    }
  };

  const makePaymentWithNewAccount = async () => {
    try {
      if (!stripe || !elements) {
        throw Error("Stripe hasn't been loaded");
      }

      setProcessing(true);

      const { error: submitError } = await elements.submit();

      if (submitError) {
        setSubmitError(submitError.message || '');
        return;
      }

      const stripeResponse = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email: values.email,
              name: values.name,
              address: values.address,
              phone: values.phone,
            },
          },
        },
        redirect: 'if_required',
        clientSecret,
      });

      if (stripeResponse.error) {
        throw stripeResponse.error;
      }

      timeoutId = setPaymentTimeout();

      await getPaymentReview(stripeResponse.paymentIntent.id);

      onClose();
      return stripeResponse;
    } catch (error: any) {
      onClose();
      notify(error?.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const addNewAccount = async () => {
    try {
      if (!stripe || !elements) {
        throw Error("Stripe hasn't been loaded");
      }

      setProcessing(true);

      const { error: submitError } = await elements.submit();

      if (submitError) {
        throw submitError;
      }

      const { setupIntent: data, error: cardSetupError } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email: values.email,
              name: values.name,
              address: values.address,
              phone: values.phone,
            },
          },
        },
        redirect: 'if_required',
      });

      if (cardSetupError) throw cardSetupError;

      const intentId = data!.id;

      const result = await dataProvider.post('setupIntent', {
        data: {
          setupIntentId: intentId,
          source: 'AUTO_REFILL',
          billingAddress: {
            ...getAll(values, {
              addressLine1: 'address.line1',
              addressLine2: 'address.line2',
              city: 'address.city',
              stateProvinceCode: 'address.state',
              countryCode: 'address.country',
              postalCode: 'address.postal_code',
              name: 'name',
              email: 'email',
              phone: 'phone',
            }),
          },
        },
      });

      onAccountConnect();

      return result;
    } catch (error: any) {
      setSubmitError(error?.message ?? error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = () => {
    setSubmitError('');
    if (pageTitle === ADD_ACCOUNT) {
      return addNewAccount();
    } else if (pageTitle === ADD_BALANCE) {
      return makePaymentWithNewAccount();
    }
  };

  return (
    <>
      <GroupTitle className={classes.addressGroupTitle} icon={<BankIcon />}>
        Bank account
      </GroupTitle>

      <Box className={classes.bankListContainer}>
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                name: 'never',
                email: 'never',
              },
            },
          }}
          onReady={() => setButtonEnabled(true)}
        />
      </Box>

      {!!submitError && (
        <FormLabel className={classes.error} component="div" error>
          {submitError}
        </FormLabel>
      )}

      <Box className={classes.actions}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              size="large"
              onClick={onCancel}
              className={classes.dialogButton}>
              Cancel
            </Button>
          </Grid>

          <Grid item xs={6}>
            <SubmitButton
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              label="Submit"
              submitting={processing}
              uppercase={false}
              progress={pageTitle === ADD_BALANCE ? paymentLoader : undefined}
              disabled={!buttonEnabled}
              onClick={handleSubmit}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const AddBankAccount = ({
  onCancel,
  amount,
  paymentLoader,
  onClose,
  pageTitle,
  stripe,
  onAccountConnect,
}: Props) => {
  const dataProvider = useDataProvider();
  const classes = useStyles();
  const { profile } = useProfile();

  const domesticCountryCode = getDomesticCountryCode();

  const [warehouse, setWarehouse] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string>('');

  useEffect(() => {
    const getWarehouse = async () => {
      try {
        const { data: defaultWarehouse } = await dataProvider.get('warehouseDefault');

        if (defaultWarehouse) {
          setWarehouse({
            country: domesticCountryCode,
            line1: defaultWarehouse.addressLine1,
            line2: defaultWarehouse.addressLine2,
            city: defaultWarehouse.city,
            state: defaultWarehouse.stateOrProvince?.code,
            postal_code: defaultWarehouse.postalCode,
          });

          return;
        }

        const { data: warehousePresets } = await dataProvider.getList('warehousePresets', {
          pagination: { perPage: 1 },
        });

        setWarehouse({
          country: domesticCountryCode,
          line1: warehousePresets?.[0]?.addressLine1,
          line2: warehousePresets?.[0]?.addressLine2,
          city: warehousePresets?.[0]?.city,
          state: warehousePresets?.[0]?.stateOrProvince?.code,
          postal_code: warehousePresets?.[0]?.postalCode,
        });
      } catch (error: any) {
        console.error(error?.message ?? error);
      }
    };

    getWarehouse();
  }, [dataProvider, domesticCountryCode]);

  const [showParser, setShowParser] = useState<boolean>(false);

  const getPaymentIntent = async (values: any) => {
    try {
      const {
        data: { clientKey },
      } = await dataProvider.post('paymentIntents', {
        data: {
          billingAddress: {
            ...getAll(values, {
              addressLine1: 'address.line1',
              addressLine2: 'address.line2',
              city: 'address.city',
              stateProvinceCode: 'address.state',
              countryCode: 'address.country',
              postalCode: 'address.postal_code',
              name: 'name',
              email: 'email',
              phone: 'phone',
            }),
          },
          customAmount: amount,
          paymentMethodType: 'US_BANK_ACCOUNT',
          saveCard: values.saveCard,
        },
      });

      setClientSecret(clientKey);

      return clientKey;
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    }
  };

  const getSetupIntent = async (values: any) => {
    try {
      const {
        data: { clientKey },
      } = await dataProvider.getOne('setupIntent', { id: 'US_BANK_ACCOUNT' });

      setClientSecret(clientKey);

      return clientKey;
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    }
  };

  const handleSubmitForm = async (values) => {
    if (pageTitle === ADD_BALANCE) {
      return getPaymentIntent(values);
    } else if (pageTitle === ADD_ACCOUNT) {
      return getSetupIntent(values);
    }
  };

  const initialValues = useMemo(
    () => ({
      address: warehouse || {},
      name: profile ? `${profile.given_name} ${profile.family_name}` : '',
      email: profile?.email ?? '',
      phone: profile?.phone_number ?? '',
      saveCard: true,
    }),
    [warehouse, profile]
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ submitting, handleSubmit, values, form, ...renderProps }) => {
        if (!!clientSecret) {
          return (
            <Elements stripe={stripe} options={{ clientSecret }}>
              <PaymentSection
                onCancel={onCancel}
                onClose={onClose}
                clientSecret={clientSecret}
                paymentLoader={paymentLoader}
                pageTitle={pageTitle}
                values={values}
                onAccountConnect={onAccountConnect}
              />
            </Elements>
          );
        }

        return (
          <form onSubmit={handleSubmit}>
            <Group classes={{ group: classes.group }}>
              <GroupTitle className={classes.addressGroupTitle} icon={<ToLocation />}>
                <Grid container justify={'space-between'} alignItems={'flex-start'}>
                  <Grid item>Billing address</Grid>
                  <Grid item>
                    <Button
                      size="small"
                      className={classes.button}
                      endIcon={showParser ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      onClick={() => setShowParser(!showParser)}>
                      {`Paste ${domesticCountryCode} Address`}
                    </Button>
                  </Grid>
                </Grid>
              </GroupTitle>
              <GroupContent className={classes.groupContent}>
                {showParser && (
                  <GroupItem wide>
                    <Input
                      name="string"
                      label={`Paste or type ${domesticCountryCode} address here...`}
                      multiline={true}
                    />
                    <AddressDistributor
                      field="string"
                      targets={{
                        addressLine1: 'address.line1',
                        addressLine2: 'address.line2',
                        city: 'address.city',
                        stateProvinceCode: 'address.state',
                        zipCode: 'address.postal_code',
                      }}
                      form={form}
                    />
                  </GroupItem>
                )}
                <GroupItem>
                  <Input name="address.line1" label="Address Line 1" validate={required} />
                </GroupItem>
                <GroupItem>
                  <Input name="address.line2" label="Address Line 2" />
                </GroupItem>
                <GroupItem>
                  <Input name="address.city" label="City / Town" validate={required} />
                </GroupItem>
                <GroupItem>
                  <StateSelect
                    label="State"
                    name="address.state"
                    countryCode={get(values, 'address.country')}
                    validate={required}
                  />
                </GroupItem>
                <GroupItem>
                  <Input label="ZIP" name="address.postal_code" validate={required} />
                </GroupItem>
                <GroupItem>
                  <CountriesSelect
                    label="Country"
                    name="address.country"
                    disabled={true}
                    validate={required}
                  />
                </GroupItem>
              </GroupContent>
            </Group>

            <Box className={classes.saveCard}>
              <Typography>Save bank account details</Typography>
              <Checkbox
                classes={{ root: classes.checkboxRoot }}
                color="primary"
                name="saveCard"
                label=""
                fullWidth={false}
                inputProps={{ disabled: pageTitle === ADD_ACCOUNT }}
              />
            </Box>

            <Typography className={classes.secondaryText}>
              You can securely store your bank details on Stripe to make future checkouts faster.
              Saving a Bank Account is required for Auto-refill.
            </Typography>

            <FormErrorLabel className={classes.error} {...renderProps} />

            <Box className={classes.actions}>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={onCancel}
                    className={classes.dialogButton}>
                    Cancel
                  </Button>
                </Grid>

                <Grid item xs={6}>
                  <SubmitButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    label="Continue"
                    type="submit"
                    submitting={submitting}
                    uppercase={false}
                  />
                </Grid>
              </Grid>
            </Box>
          </form>
        );
      }}
    />
  );
};

export default AddBankAccount;
